
import React, { useContext } from 'react';

import { type Vertex2, Components } from '@rta/rta-blueprint-library';

import { AppContext } from '@r-app/app-context';
import { DisplayMode } from '@r-core';
import { renderTracking } from '@r-services/render-tracking';
import { svgPolygonFromVectors } from '../helpers/svg-polygon-from-vectors';
import { convertPolyPoints2Vectors, getPanelItemVectors } from '@r-core/helpers';
import { Layer, type Vector } from '@r-core/types';

type RenderCountertopMaskProps = {
	islandId: string | null;
}

/**
 * Extends the front (max-y) vertices of a cutout polygon forward by a fixed bleed amount.
 * This ensures the cutout mask fully covers the countertop overhang edge, preventing slivers
 * caused by slight asymmetries in the countertop outline. Only used for 2D SVG masks where
 * extending past the countertop edge has no visual effect (black outside white is invisible).
 */
const extendMaskFront = (vectors: Array<Vector<Vertex2>>, bleed: number): Array<Vector<Vertex2>> => {
	const maxY = Math.max(...vectors.flatMap((v) => [v.from.y, v.to.y]));
	const threshold = maxY - 0.01;
	return vectors.map((v) => {
		const fromExtended = v.from.y >= threshold;
		const toExtended = v.to.y >= threshold;
		if (!fromExtended && !toExtended) return v;
		return {
			...v,
			from: fromExtended ? { ...v.from, x: v.from.x, y: v.from.y + bleed } as Vertex2 : v.from,
			to: toExtended ? { ...v.to, x: v.to.x, y: v.to.y + bleed } as Vertex2 : v.to
		};
	});
};

export const RenderCountertopMask = ({ islandId }: RenderCountertopMaskProps): React.ReactElement => {
	const { controller: { kitchen, navigation }} = useContext(AppContext);
	const island = kitchen.design.islands.findById(islandId);

	renderTracking.incrementRender('RenderIslandOverheadMask');

	const countertop = island.getCountertop();
	const renderDetails = countertop.renderingDetails;
	const countertopVectors = convertPolyPoints2Vectors(renderDetails);

	// In any manufacturing view, skip top-only appliances (pizza ovens, asado
	// smokers, etc.) when building the mask — they sit on top of the countertop
	// and don't cut through, so they shouldn't show as a hole in the slab.
	const isMfgView = navigation.displayMode === DisplayMode.Manufacturing
		|| navigation.displayMode === DisplayMode.InlineManufacturing
		|| navigation.displayMode === DisplayMode.OverheadManufacturing
		|| navigation.displayMode === DisplayMode.BacksplashManufacturing
		|| navigation.displayMode === DisplayMode.SkuOverheadManufacturing;

	/* Create mask data for island panelItems with a distanceAboveCountertop > 0 */
	const islandMaskData = island.panels.children.map((panel) => {
		return {
			id: panel.id,
			center: panel.center,
			angle: -panel.getOriginAngle(),
			panelItemGroups: panel.panelItemGroups.children.map((panelItemGroup) => {
				return {
					id: panelItemGroup.id,
					center: panelItemGroup.center,
					angle: 0,
					panelItems: panelItemGroup.items.children.map((panelItem) => {
						const isTopOnly = panelItem.item.placement === Components.PLACEMENT_TOP_ONLY;
						if (isTopOnly && isMfgView && !panelItem.item.distanceBelowCountertop) return null;
						const rawCorners = getPanelItemVectors({ panelItem, isCountertop: true }).filter((v) => v.layer === Layer.Panel);
						const needsBleed = panelItem.item.distanceFrontOfFace !== null
							&& panelItem.item.distanceFrontOfFace < (panel.countertopOverhang ?? 2);
						const corners = needsBleed ? extendMaskFront(rawCorners, 1) : rawCorners;
						// Only cut a hole for items that actually penetrate the countertop.
						// Top-only items that just sit on the surface (distanceBelowCountertop is null/0)
						// should NOT create a mask hole.
						const cutsIntoCountertop = (panelItem.item.distanceBelowCountertop ?? 0) > 0;
						// Instance rotation for the mask cutout (negate for SVG coordinate system)
						const { z: irZ } = panelItem.effectiveRotation;
						const maskAngle = irZ !== 0 ? -(irZ * (180 / Math.PI)) : 0;
						if (panelItem.item.distanceAboveCountertop > 0 && cutsIntoCountertop) {
							return {
								id: panelItem.id,
								center: panelItem.center,
								angle: maskAngle,
								svgPolygon: panelItem.item.component.svg?.cutout({ fill: 'black', opacity: 1, diameter: panelItem.item.cutout.width }) ?? svgPolygonFromVectors(corners, { fill: 'black', opacity: 1 })
							};
						}
						return null;
					}).filter((item) => item !== null)
				};
			}).filter((group) => group.panelItems.length > 0)
		};
	}).filter((group) => group.panelItemGroups.length > 0);

	/* Render the masks for island panelItem Cutouts */
	const masks = islandMaskData.map((data) => {
		return <g
			key={`${data.id}-mask`}
			type="island-panel-group-mask"
			transform={`translate(${data.center.x}, ${data.center.z}) rotate(${data.angle})`}
		>{
			data.panelItemGroups.map((group) => {
				return <g
					key={`${group.id}-mask`}
					type="island-panel-item-group-mask"
					transform={`translate(${group.center.x}, ${group.center.z}) rotate(${group.angle})`}
				>{
					group.panelItems.map((item) => {
						return <g
							key={`${item.id}-mask`}
							type="island-panel-item-mask"
							transform={`translate(${item.center.x}, ${item.center.z}) rotate(${item.angle})`}
						>{
							item.svgPolygon
						}</g>;
					})
				}</g>;
			})
		}</g>;
	});

	/* Render the masks for island panelItem Cutouts */
	return <defs>
		<mask id={`countertop-mask-${islandId}`}>
			{/* <rect width={island.islandSize.width} height={island.islandSize.depth} fill="white" /> */}
			{ svgPolygonFromVectors(countertopVectors, { fill: 'white' }) }
			{ masks }
		</mask>
	</defs>;
};
